import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react';
import { useCMSContent } from '../hooks/useCMSContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactPage = () => {
  // Fetch CMS content
  const { data: companyInfo, loading: companyLoading } = useCMSContent('companyInfo');
  const { data: serviceAreas, loading: areasLoading } = useCMSContent('serviceAreas');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const data = await response.json();
      console.log('Contact submission successful:', data);
      
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setError('Failed to submit your message. Please try again or email us directly at ' + (companyInfo?.email || 'Office@lrmgmt.net'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-page)' }} data-testid="contact-page">
      {/* Hero */}
      <section style={{
        backgroundColor: 'var(--bg-card)',
        padding: '100px 0 80px'
      }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h1 className="heading-1" style={{ marginBottom: '24px' }}>
              Contact Our Office
            </h1>
            <p className="body-large" style={{ color: 'var(--neutral-light)' }}>
              Have questions about our property management services? We're here to help. Reach out to discuss your needs.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-spacing">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '60px',
            alignItems: 'start'
          }}>
            {/* Contact Form */}
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '50px',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h2 style={{
                fontFamily: 'Arial, sans-serif',
                fontWeight: 700,
                fontSize: '1.75rem',
                color: 'var(--brand-primary)',
                marginBottom: '12px',
                textTransform: 'uppercase'
              }}>
                Send Us a Message
              </h2>
              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '1rem',
                marginBottom: '30px'
              }}>
                Fill out the form below and we'll get back to you promptly.
              </p>

              {submitted ?
              <div style={{
                padding: '40px',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #86efac'
              }}>
                  <CheckCircle size={60} color="#10b981" style={{ marginBottom: '20px', display: 'inline-block' }} />
                  <h3 style={{
                  color: '#166534',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  marginBottom: '12px'
                }}>
                    Thank You!
                  </h3>
                  <p style={{
                  color: '#166534',
                  fontSize: '1.05rem'
                }}>
                    We've received your message and will respond shortly.
                  </p>
                </div> :

              <>
                {error && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef2f2',
                    borderRadius: '8px',
                    marginBottom: '24px',
                    border: '1px solid #fecaca'
                  }}>
                    <p style={{
                      color: '#991b1b',
                      fontSize: '0.9375rem',
                      margin: 0
                    }}>
                      {error}
                    </p>
                  </div>
                )}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div>
                    <label style={{
                    display: 'block',
                    color: 'var(--neutral-light)',
                    fontSize: '0.95rem',
                    marginBottom: '8px',
                    fontWeight: 500
                  }}>
                      Your Name *
                    </label>
                    <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      backgroundColor: 'var(--bg-page)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '8px',
                      color: 'var(--neutral-light)',
                      fontSize: '1rem',
                      fontFamily: 'Inter, Arial, sans-serif',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'} />

                  </div>

                  <div>
                    <label style={{
                    display: 'block',
                    color: 'var(--neutral-light)',
                    fontSize: '0.95rem',
                    marginBottom: '8px',
                    fontWeight: 500
                  }}>
                      Email Address *
                    </label>
                    <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      backgroundColor: 'var(--bg-page)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '8px',
                      color: 'var(--neutral-light)',
                      fontSize: '1rem',
                      fontFamily: 'Inter, Arial, sans-serif',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'} />

                  </div>

                  <div>
                    <label style={{
                    display: 'block',
                    color: 'var(--neutral-light)',
                    fontSize: '0.95rem',
                    marginBottom: '8px',
                    fontWeight: 500
                  }}>
                      Phone Number
                    </label>
                    <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      backgroundColor: 'var(--bg-page)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '8px',
                      color: 'var(--neutral-light)',
                      fontSize: '1rem',
                      fontFamily: 'Inter, Arial, sans-serif',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'} />

                  </div>

                  <div>
                    <label style={{
                    display: 'block',
                    color: 'var(--neutral-light)',
                    fontSize: '0.95rem',
                    marginBottom: '8px',
                    fontWeight: 500
                  }}>
                      Subject *
                    </label>
                    <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Property Management Inquiry"
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      backgroundColor: 'var(--bg-page)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '8px',
                      color: 'var(--neutral-light)',
                      fontSize: '1rem',
                      fontFamily: 'Inter, Arial, sans-serif',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'} />

                  </div>

                  <div>
                    <label style={{
                    display: 'block',
                    color: 'var(--neutral-light)',
                    fontSize: '0.95rem',
                    marginBottom: '8px',
                    fontWeight: 500
                  }}>
                      Message *
                    </label>
                    <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us about your property management needs..."
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      backgroundColor: 'var(--bg-page)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '8px',
                      color: 'var(--neutral-light)',
                      fontSize: '1rem',
                      fontFamily: 'Inter, Arial, sans-serif',
                      resize: 'vertical',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'} />

                  </div>

                  <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
                    {loading ? 'Sending...' : 'Send Message'}
                    {!loading && <Send size={20} style={{ marginLeft: '8px' }} />}
                  </button>
                </form>
              </>
              }
            </div>

            {/* Contact Information */}
            <div>
              <div style={{
                backgroundColor: 'var(--bg-card)',
                padding: '50px',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                marginBottom: '30px'
              }}>
                <h2 style={{
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.75rem',
                  color: 'var(--brand-primary)',
                  marginBottom: '30px',
                  textTransform: 'uppercase'
                }}>
                  Contact Information
                </h2>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      marginBottom: '10px'
                    }}>
                      <Mail size={28} color="var(--brand-primary)" strokeWidth={1.5} />
                      <div>
                        <p style={{
                          color: 'var(--text-secondary)',
                          fontSize: '0.75rem',
                          margin: '0 0 4px 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontWeight: 600
                        }}>
                          Email
                        </p>
                        <a href={`mailto:${companyInfo?.email || 'Office@lrmgmt.net'}`} style={{
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          textDecoration: 'none',
                          fontWeight: 500,
                          transition: 'color 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.color = 'var(--brand-primary)'}
                        onMouseLeave={(e) => e.target.style.color = 'var(--text-primary)'}
                        >
                          {companyInfo?.email || 'Office@lrmgmt.net'}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      marginBottom: '10px'
                    }}>
                      <Phone size={28} color="var(--brand-primary)" strokeWidth={1.5} />
                      <div>
                        <p style={{
                          color: 'var(--text-secondary)',
                          fontSize: '0.75rem',
                          margin: '0 0 4px 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontWeight: 600
                        }}>
                          Phone
                        </p>
                        <p style={{
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          margin: 0,
                          fontWeight: 500
                        }}>
                          {companyInfo?.phone || '(606) 683-3100'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      marginBottom: '10px'
                    }}>
                      <Phone size={28} color="var(--brand-primary)" strokeWidth={1.5} />
                      <div>
                        <p style={{
                          color: 'var(--text-secondary)',
                          fontSize: '0.75rem',
                          margin: '0 0 4px 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontWeight: 600
                        }}>
                          Fax
                        </p>
                        <p style={{
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          margin: 0,
                          fontWeight: 500
                        }}>
                          {companyInfo?.fax || '(606) 683-3300'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '15px'
                    }}>
                      <MapPin size={28} color="var(--brand-primary)" strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <div>
                        <p style={{
                          color: 'var(--text-secondary)',
                          fontSize: '0.75rem',
                          margin: '0 0 8px 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontWeight: 600
                        }}>
                          Business Hours
                        </p>
                        <p style={{
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          margin: 0,
                          lineHeight: 1.6
                        }}>
                          {companyInfo?.businessHours || 'Monday - Friday: 9:00 AM - 5:00 PM'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Areas Map */}
              <div style={{
                backgroundColor: 'var(--secondary-olive)',
                padding: '40px',
                borderRadius: '12px'
              }}>
                <h3 style={{
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  color: 'var(--brand-primary)',
                  marginBottom: '20px',
                  textTransform: 'uppercase'
                }}>
                  Service Areas
                </h3>
                <p style={{
                  color: 'var(--neutral-light)',
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  marginBottom: '20px'
                }}>
                  We provide professional property management services throughout the region.
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '12px'
                }}>
                  {(serviceAreas || []).slice(0, 4).map((area, index) =>
                  <div key={area.id || area.documentId || index} style={{
                    color: 'var(--neutral-light)',
                    fontSize: '0.95rem',
                    padding: '8px 0',
                    borderBottom: '1px solid var(--border-light)'
                  }}>
                      {area.name}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map/Location Section - Mock */}
      <section style={{
        backgroundColor: 'var(--bg-card)',
        padding: '80px 0'
      }}>
        <div className="container">
          <div style={{
            backgroundColor: 'var(--bg-page)',
            height: '400px',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: `url('https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxjaXR5c2NhcGV8ZW58MHx8fHwxNzcwNDExODY4fDA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(26, 28, 27, 0.7)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <MapPin size={60} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                <h3 style={{
                  color: 'var(--brand-primary)',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  marginBottom: '12px'
                }}>
                  Professional Property Management
                </h3>
                <p style={{
                  color: 'var(--neutral-light)',
                  fontSize: '1.1rem'
                }} className="!text-[#5C5CC5]">
                  Serving properties throughout the region
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>);

};